/**
 * 开始节点配置面板
 *
 * 配置工作流的起始任务 prompt
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import type { StartNodeData } from '../../../types/workflow';
import { useWorkflowStore } from '../../../store/workflowStore';

export interface StartNodeConfigPanelProps {
  /** 节点 ID */
  nodeId: string;
}

export const StartNodeConfigPanel: React.FC<StartNodeConfigPanelProps> = React.memo(({
  nodeId,
}) => {
  // 从 store 获取节点数据
  const node = useWorkflowStore((state) =>
    state.nodes.find((n) => n.id === nodeId)
  );
  const updateNodeForm = useWorkflowStore((state) => state.updateNodeForm);

  // 本地表单状态
  const [formData, setFormData] = useState<{
    label: string;
    task_prompt: string;
  }>({
    label: '',
    task_prompt: '',
  });

  // 错误状态
  const [errors, setErrors] = useState<{
    task_prompt?: string;
  }>({});

  // 同步节点数据到本地状态
  useEffect(() => {
    if (node) {
      const data = node.data as StartNodeData;
      setFormData({
        label: data.label || '开始',
        task_prompt: data.task_prompt || '',
      });
    }
  }, [node]);

  // 验证表单
  const validateForm = useCallback(() => {
    const newErrors: typeof errors = {};

    if (!formData.task_prompt.trim()) {
      newErrors.task_prompt = '任务描述不能为空';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // 保存配置
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    updateNodeForm(nodeId, {
      label: formData.label,
      task_prompt: formData.task_prompt.trim(),
    });
  }, [nodeId, formData, updateNodeForm, validateForm]);

  // 更新字段
  const updateField = useCallback(<K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // 清除对应错误（仅针对有验证的字段）
    if (field === 'task_prompt' && errors.task_prompt) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.task_prompt;
        return next;
      });
    }
  }, [errors]);

  return (
    <div className="p-4 space-y-4">
      {/* 节点名称 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          节点名称
        </label>
        <input
          type="text"
          value={formData.label}
          onChange={(e) => updateField('label', e.target.value)}
          placeholder="开始"
          className={clsx(
            'w-full px-3 py-2 rounded-md border border-gray-200',
            'focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent',
            'text-sm text-gray-700',
          )}
        />
      </div>

      {/* 任务描述 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          任务描述 <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.task_prompt}
          onChange={(e) => updateField('task_prompt', e.target.value)}
          onBlur={validateForm}
          placeholder="输入任务描述，例如：帮我创作第一章的内容..."
          rows={6}
          className={clsx(
            'w-full px-3 py-2 rounded-md border resize-none',
            'focus:outline-none focus:ring-2 focus:border-transparent',
            'text-sm text-gray-700 placeholder:text-gray-400',
            errors.task_prompt
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-200 focus:ring-green-500',
          )}
        />
        {errors.task_prompt && (
          <p className="mt-1 text-sm text-red-500">{errors.task_prompt}</p>
        )}
        <p className="mt-1.5 text-xs text-gray-400">
          描述工作流的总体任务目标，执行时会自动注入工作区环境信息
        </p>
      </div>

      {/* 变量引用提示 */}
      <div className="p-3 bg-gray-50 rounded-md">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          自动注入的环境变量
        </div>
        <div className="space-y-1.5 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{workspace.book_name}'}</code>
            <span>工作区书名</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{workspace.total_chapters}'}</code>
            <span>总章节数</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{'{workspace.genre}'}</code>
            <span>作品类型</span>
          </div>
        </div>
      </div>

      {/* 保存按钮 */}
      <button
        onClick={handleSave}
        className={clsx(
          'w-full py-2 px-4 rounded-md text-sm font-medium',
          'bg-green-500 text-white',
          'hover:bg-green-600 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
        )}
      >
        保存配置
      </button>
    </div>
  );
});

StartNodeConfigPanel.displayName = 'StartNodeConfigPanel';

export default StartNodeConfigPanel;
